import { AutoAwesomeOutlined } from "@mui/icons-material";
import {
    Box,
    Paper,
    Skeleton,
    Stack,
    Typography,
    useTheme,
} from "@mui/material";
import { formatShortDate } from "components/Admin/Organizations/OrganizationDetail/OrganizationClientBrain/clientBrainUtils";
import UserContextStore from "components/Admin/Organizations/OrganizationDetail/OrganizationClientBrain/UserContextStore";
import UserContextFieldsEditor from "components/KBo/ClientBrain/UserContextFieldsEditor";
import { SubscriptionCadence } from "components/Conversations/Views/Drawer/components/Subscriptions/helpers";
import MessageStore from "components/ManagerInteractions/Stores/MessageStore";
import AcxButton from "components/UI/AcxButton";
import AcxChip from "components/UI/AcxChip";
import AcxToggleButton from "components/UI/AcxToggleButton";
import { observer } from "mobx-react";
import React from "react";
import ClientBrainService from "services/ClientBrainService";
import {
    KboFlowSubscription,
    KboFlowSubscriptionsService,
} from "services/KboFlowSubscriptionsService";
import { useStore } from "utils/useStore";

const cadenceOptions = [
    {
        id: "kbo-flow-user-subscription-weekly",
        value: SubscriptionCadence.Weekly,
        element: "Weekly",
    },
    {
        id: "kbo-flow-user-subscription-monthly",
        value: SubscriptionCadence.Monthly,
        element: "Monthly",
    },
];

const formatDateTime = (value?: string | null) =>
    value ? formatShortDate(value) : undefined;

/**
 * Self-service "User settings" tab under KBo Settings. Lets the current user manage their own
 * KBo Flow email subscription and the same per-user Client Brain context (Job role, Goals,
 * Responsibilities, Success Metrics, Current Concerns) that admins edit for any user from the
 * Client Brain "User Profiles" section — both surfaces read/write the same record.
 */
const UserSettingsPage = observer(function UserSettingsPage() {
    const theme = useTheme();
    const messageStore = useStore(MessageStore);
    const clientBrainService = React.useMemo(
        () => new ClientBrainService(),
        [],
    );
    const subscriptionsService = React.useMemo(
        () => new KboFlowSubscriptionsService(),
        [],
    );

    const [loading, setLoading] = React.useState(true);
    const [contextStore, setContextStore] = React.useState<UserContextStore>();
    const [subscription, setSubscription] =
        React.useState<KboFlowSubscription | null>(null);
    const [subscriptionSaving, setSubscriptionSaving] = React.useState(false);

    React.useEffect(() => {
        let cancelled = false;

        Promise.all([
            clientBrainService.getMyUserContext(),
            subscriptionsService.getMine(),
        ])
            .then(([profile, mySubscription]) => {
                if (cancelled) return;
                setContextStore(
                    new UserContextStore(
                        profile,
                        messageStore,
                        (_userId, payload) =>
                            clientBrainService.updateMyUserContext(payload),
                    ),
                );
                setSubscription(mySubscription);
            })
            .catch((error) => {
                if (!cancelled) {
                    messageStore.logError(
                        error instanceof Error
                            ? error.message
                            : "User settings could not be loaded.",
                    );
                }
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });

        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const changeCadence = async (cadence: SubscriptionCadence) => {
        setSubscriptionSaving(true);
        try {
            const updated = await subscriptionsService.subscribeMine(cadence);
            setSubscription(updated);
        } catch (error) {
            messageStore.logError(
                error instanceof Error
                    ? error.message
                    : "The subscription could not be saved.",
            );
        } finally {
            setSubscriptionSaving(false);
        }
    };

    const turnOff = async () => {
        setSubscriptionSaving(true);
        try {
            await subscriptionsService.unsubscribeMine();
            setSubscription(null);
        } catch (error) {
            messageStore.logError(
                error instanceof Error
                    ? error.message
                    : "The subscription could not be updated.",
            );
        } finally {
            setSubscriptionSaving(false);
        }
    };

    return (
        <Stack rowGap={2} padding={3} width="100%">
            <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="flex-start"
                columnGap={2}
            >
                <Stack rowGap={0.5}>
                    <Typography variant="h5" fontWeight={600}>
                        User settings
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Manage your KBo Flow subscription and the context KBo
                        uses to decide what to research and how to frame
                        findings for you. Update either any time.
                    </Typography>
                </Stack>
                {contextStore &&
                    (contextStore.isEditing ? (
                        <Stack direction="row" columnGap={1} flexShrink={0}>
                            <AcxButton
                                color="white"
                                onClick={() => contextStore.cancelEdit()}
                                disabled={contextStore.isSaving}
                            >
                                Cancel
                            </AcxButton>
                            <AcxButton
                                color="primary"
                                onClick={() => contextStore.save()}
                                loading={contextStore.isSaving}
                            >
                                Save changes
                            </AcxButton>
                        </Stack>
                    ) : (
                        <AcxButton
                            color="primary"
                            variant="text"
                            onClick={() => contextStore.startEdit()}
                        >
                            Edit context
                        </AcxButton>
                    ))}
            </Stack>

            {loading || !contextStore ? (
                <Skeleton variant="rounded" height={480} />
            ) : (
                <>
                    <Paper
                        id="kbo-flow-user-subscription"
                        variant="outlined"
                        sx={{
                            padding: theme.spacing(2),
                            borderRadius: "8px",
                        }}
                    >
                        <Stack
                            direction="row"
                            justifyContent="space-between"
                            alignItems="flex-start"
                            columnGap={2}
                        >
                            <Stack
                                direction="row"
                                columnGap={1.5}
                                alignItems="center"
                            >
                                <Box
                                    sx={{
                                        width: 36,
                                        height: 36,
                                        borderRadius: "8px",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                                        color: theme.palette.white.main,
                                    }}
                                >
                                    <AutoAwesomeOutlined fontSize="small" />
                                </Box>
                                <Typography fontWeight={600}>
                                    KBo Flow
                                </Typography>
                                <AcxChip
                                    size="small"
                                    color={subscription ? "blue" : "gray"}
                                    label={
                                        subscription
                                            ? "Subscribed"
                                            : "Not subscribed"
                                    }
                                />
                            </Stack>
                        </Stack>

                        <Typography
                            color="text.secondary"
                            sx={{
                                marginTop: theme.spacing(0.5),
                                marginBottom: theme.spacing(1.5),
                            }}
                        >
                            KBo keeps researching after you close the chat and
                            emails you what changed in your program, the
                            conversations behind it, and what to do next.
                        </Typography>

                        <Stack
                            direction="row"
                            justifyContent="space-between"
                            alignItems="center"
                            flexWrap="wrap"
                            rowGap={1.5}
                        >
                            <Stack
                                direction="row"
                                alignItems="center"
                                columnGap={1.5}
                            >
                                <Typography fontWeight={600}>
                                    Send it
                                </Typography>
                                <AcxToggleButton
                                    id="user-settings-subscription-cadence"
                                    defaultValue={
                                        subscription?.cadence ??
                                        SubscriptionCadence.Weekly
                                    }
                                    exclusive
                                    items={cadenceOptions}
                                    disabled={subscriptionSaving}
                                    onChange={(value) => {
                                        if (value !== null) {
                                            void changeCadence(
                                                value as SubscriptionCadence,
                                            );
                                        }
                                    }}
                                />
                            </Stack>
                            <Stack
                                direction="row"
                                alignItems="center"
                                columnGap={2}
                            >
                                <Typography color="text.secondary">
                                    {subscription
                                        ? `Last updated ${
                                              formatDateTime(
                                                  subscription.modifiedOn,
                                              ) ?? "recently"
                                          }`
                                        : "No emails scheduled"}
                                </Typography>
                                <AcxButton
                                    id={
                                        subscription
                                            ? "kbo-flow-user-subscription-turn-off"
                                            : "kbo-flow-user-subscription-turn-on"
                                    }
                                    color={subscription ? "white" : "primary"}
                                    loading={subscriptionSaving}
                                    onClick={() =>
                                        subscription
                                            ? turnOff()
                                            : changeCadence(
                                                  SubscriptionCadence.Weekly,
                                              )
                                    }
                                >
                                    {subscription ? "Turn off" : "Turn on"}
                                </AcxButton>
                            </Stack>
                        </Stack>
                    </Paper>

                    <Stack rowGap={1}>
                        <Stack
                            direction="row"
                            justifyContent="space-between"
                            alignItems="baseline"
                            columnGap={2}
                        >
                            <Typography
                                variant="overline"
                                sx={{
                                    color: theme.palette.gray[400],
                                    fontWeight: 600,
                                    letterSpacing: "0.06em",
                                }}
                            >
                                Your context
                            </Typography>
                            {(contextStore.context.modifiedBy ||
                                contextStore.context.modifiedOn) && (
                                <Typography
                                    sx={{
                                        fontSize: "12px",
                                        color: theme.palette.gray[400],
                                    }}
                                >
                                    Last updated by{" "}
                                    {contextStore.context.modifiedBy} ·{" "}
                                    {formatShortDate(
                                        contextStore.context.modifiedOn,
                                    )}
                                </Typography>
                            )}
                        </Stack>
                        <Paper
                            id="kbo-user-context"
                            variant="outlined"
                            sx={{
                                padding: theme.spacing(2),
                                borderRadius: "8px",
                            }}
                        >
                            <UserContextFieldsEditor
                                ctx={
                                    contextStore.isEditing
                                        ? contextStore.draft
                                        : contextStore.context
                                }
                                isEditing={contextStore.isEditing}
                                onSetRole={(value) =>
                                    contextStore.setRole(value)
                                }
                                onSetListFieldText={(field, text) =>
                                    contextStore.setListFieldText(field, text)
                                }
                                keyPrefix={contextStore.userId}
                            />
                        </Paper>
                    </Stack>
                </>
            )}
        </Stack>
    );
});

export default UserSettingsPage;
